// Phase 1F -- Results: baseline, then intervention, then outcome.
// A valid OBSERVED check with no approved work is a visibility baseline,
// not "nothing measured" and not a business outcome. Failed / UNKNOWN
// checks are not a baseline. Results come only from real checks.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { somHistoryAPI } from '@/lib/api';
import { checkState, formatMeasurementState, formatMentions } from '@/lib/somHistoryHelpers';
import { useFounderWorkspace, useFounderPath } from '../FounderWorkspace';
import { Chip, EmptyState, Eyebrow, Loading, PageHeader, PrimaryButton, Section, TextButton, SecondaryButton, formatDate } from '../ui';
import { measurementStage } from '../measurementStage';
import { resultsPageCopy } from '../resultsCopy';

const RESULT = {
  not_started: { label: 'Not measuring yet', tone: 'neutral' },
  too_early: { label: 'Too early', tone: 'warn' },
  improving: { label: 'Improving', tone: 'good' },
  no_change: { label: 'No change', tone: 'neutral' },
  worse: { label: 'Worse', tone: 'bad' },
  not_comparable: { label: 'Not comparable', tone: 'neutral' },
};

function StageNote({ stage, fp }) {
  return (
    <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1" data-testid="results-stage" data-stage={stage.key}>
      <Chip tone={stage.key === 'results_available' ? 'good' : stage.key === 'awaiting_questions' ? 'warn' : 'neutral'}>{stage.label}</Chip>
      <p className="text-[13px] text-[var(--text-secondary)]">{stage.detail}</p>
      {stage.action && <TextButton to={fp(stage.action.to)} tone="accent">{stage.action.label}</TextButton>}
    </div>
  );
}

function Timeline({ m, fp }) {
  const cell = 'min-w-0';
  return (
    <ol className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-5 mt-4" data-testid="results-timeline">
      <li className={cell}>
        <Eyebrow>Baseline</Eyebrow>
        <p className="mt-1 font-display text-[22px] font-bold tabular-nums">{m.baseline && m.baseline.of > 0 ? `${m.baseline.value} / ${m.baseline.of}` : '—'}</p>
        <p className="text-[12.5px] text-[var(--text-tertiary)]">{m.baseline && m.baseline.of > 0 ? `${m.baseline.of} usable answers · ${formatDate(m.baseline.observed_at)}` : 'No valid baseline yet'}</p>
      </li>
      <li className={cell}>
        <Eyebrow>Intervention</Eyebrow>
        <p className="mt-1 text-[15px] font-semibold">{m.intervention ? 'Work approved' : 'Not approved yet'}</p>
        <p className="text-[12.5px] text-[var(--text-tertiary)]">{m.intervention ? formatDate(m.intervention.at) : 'Approve the work Martian prepared'}</p>
      </li>
      <li className={cell}>
        <Eyebrow>Next check</Eyebrow>
        <p className="mt-1 text-[15px] font-semibold">{m.next_check ? formatDate(m.next_check) : 'Not scheduled'}</p>
        {!m.next_check && m.next_check_blocked_by === 'watchlist_not_approved' && (
          <TextButton to={fp('/business/watchlist')} tone="accent">Approve your questions</TextButton>
        )}
      </li>
      <li className={cell}>
        <Eyebrow>Result</Eyebrow>
        <div className="mt-1.5"><Chip tone={(RESULT[m.result] || RESULT.not_started).tone} testId="results-result">{(RESULT[m.result] || RESULT.not_started).label}</Chip></div>
        {m.latest && <p className="mt-1 text-[12.5px] text-[var(--text-tertiary)]">Latest {m.latest.value} / {m.latest.of} · {formatDate(m.latest.observed_at)}</p>}
      </li>
    </ol>
  );
}

export default function ResultsPage() {
  const { summary, workspaceId, loadingClients, displayName } = useFounderWorkspace();
  const fp = useFounderPath();
  const navigate = useNavigate();
  const [checks, setChecks] = useState(null);

  useEffect(() => {
    if (!workspaceId) return;
    setChecks(null);
    somHistoryAPI.production(workspaceId).then((res) => setChecks(res.data || [])).catch(() => setChecks([]));
  }, [workspaceId]);

  if (loadingClients || (workspaceId && (!summary || checks === null))) return <Loading />;
  // A tenant with no workspace yet has nothing to measure. `checks` only ever
  // loads for a workspace, so it is still null here -- say so honestly (same
  // pattern as Your Business) instead of reading checks.length below. A
  // workspace whose checks request failed or returned nothing is a different
  // state and falls through to "No checks yet." as before.
  if (!workspaceId) {
    return (
      <div data-testid="results-page">
        <EmptyState testId="results-no-workspace" title="No results yet"
          body="Results come from real checks after you approve work. Add your website and Martian will record a baseline, then show you what changed."
          action={<PrimaryButton to="/onboarding" testId="results-start-onboarding">Add your website</PrimaryButton>} />
      </div>
    );
  }
  const measured = (summary?.decisions || []).filter((d) => d.measurement?.supported);
  const unmeasured = (summary?.decisions || []).filter((d) => !d.measurement?.supported);
  const header = resultsPageCopy(measured, summary?.monitoring);

  return (
    <div data-testid="results-page">
      <PageHeader
        eyebrow="Results"
        title={header.title}
        summary={header.summary}
      />

      {measured.length === 0 && unmeasured.length === 0 ? (
        <EmptyState title="No decisions to measure yet" body="When Martian opens a decision, how it will be measured appears here." />
      ) : (
        measured.map((d) => (
          <Section key={d.id} testId="results-decision" className="!pt-0 mb-2">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[12.5px] text-[var(--text-tertiary)]">{d.measurement.metric}</p>
                <p className="mt-1 text-[16px] font-semibold leading-snug max-w-[62ch] text-balance">{d.found}</p>
              </div>
              <SecondaryButton onClick={() => navigate(fp(`/decisions/${encodeURIComponent(d.id)}`))}>Open decision</SecondaryButton>
            </div>
            <StageNote stage={measurementStage(d.measurement)} fp={fp} />
            <Timeline m={d.measurement} fp={fp} />
          </Section>
        ))
      )}

      {unmeasured.length > 0 && (
        <Section eyebrow="Not measured by Martian yet" testId="results-unmeasured">
          <ul className="space-y-2">
            {unmeasured.map((d) => <li key={d.id} className="text-[14px]"><span className="font-medium">{d.found}</span> <span className="text-[var(--text-tertiary)]">— {d.measurement?.note}</span></li>)}
          </ul>
        </Section>
      )}

      <Section eyebrow="Every check Martian has run" testId="results-history">
        {checks.length === 0 ? (
          <p className="text-[14px] text-[var(--text-secondary)]">No checks yet.</p>
        ) : (
          <table className="w-full text-[13.5px]">
            <thead>
              <tr className="text-left text-[12px] text-[var(--text-tertiary)]">
                <th className="py-2 font-medium">Date</th>
                <th className="py-2 font-medium">AI answers mentioning {displayName || 'you'}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border)]">
              {checks.map((c) => (
                <tr key={c.id}>
                  <td className="py-2.5">{formatDate(c.created_at, true)}</td>
                  {/* Only questions that produced an answer count; a failed one is not a "no". */}
                  <td className="py-2.5 tabular-nums">
                    {checkState(c) === 'OBSERVED' ? formatMentions(c).replace('/', ' / ') : formatMeasurementState(c)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>
    </div>
  );
}
